/**
 * Validates a post-login destination from `?next=`.
 *
 * Only a path on this site is allowed. The value travels to Discord as OAuth
 * `state` and comes back to /authenticate, which hands it to
 * `window.location` — so accepting a full URL would make this an open redirect
 * wearing a login page: `gamebot.gg/login?next=https://evil.example` would
 * send people there carrying the trust of having started at gamebot.gg.
 *
 * A leading `//` is rejected for the same reason: `//evil.example` looks like
 * a path and is a URL.
 *
 * A query string is allowed, because a caller now needs one: a reward claim
 * bounces a logged-out player through here and back to the page they were on,
 * and every profile link the bot hands out carries `?ref=`. Dropping it would
 * land them somewhere subtly different from where they clicked. The two parts
 * are checked separately — a query may hold `=`, `&` and percent-encoding, and
 * a path may not.
 *
 * The query class stays deliberately tight: no `:` and no `/`, so a full URL
 * can't ride along inside a parameter. Nothing legitimate needs them — a real
 * producer percent-encodes a value that does — and a same-origin path holding
 * `?to=https://evil.example` is one careless route away from being the open
 * redirect this module exists to prevent.
 *
 * A fragment is still rejected: nothing needs one, and the narrowest rule that
 * does the job is the one least likely to be wrong. `#` is absent from both
 * character classes below, so one can't sneak in through either half.
 *
 * @param {unknown} next
 * @returns {string|null} the path, or null if it is not one
 */
const PATH = /^\/(?!\/)[\w\-./]*$/
const QUERY = /^[\w\-.~=&%+,]*$/

export function safeNext(next) {
    if (typeof next !== 'string') return null

    const split = next.indexOf('?')
    if (split === -1) return PATH.test(next) ? next : null

    return PATH.test(next.slice(0, split)) && QUERY.test(next.slice(split + 1))
        ? next
        : null
}

export default safeNext
